import { inject } from 'vue';
import { useRoute } from 'vue-router';
import { injectTypes } from '@/Types/InjectTypes';
import { useDetailBreadcrumb } from '@/Composables/Admin/useDetailBreadcrumb';
import { crumb } from '@/Utils/Admin/BreadcrumbUtility';

function findNavTrail(items, routeName) {
    for (const item of items ?? []) {
        if (item.to?.name === routeName) {
            return [{ label: item.label, to: item.to }];
        }

        const childTrail = item.subItems?.length ? findNavTrail(item.subItems, routeName) : null;
        if (childTrail) {
            return [{ label: item.label, to: item.to ?? null }, ...childTrail];
        }
    }

    return null;
}

// Route-driven entity breadcrumb: the area + list crumbs are resolved from the navigation via
// `route.meta.breadcrumbParent` (the list route name); the screen supplies the entity title through
// `registerBreadcrumbTitle(() => <title-or-falsy>)` (falsy while the entity is not loaded).
export function useEntityBreadcrumb() {
    const route = useRoute();
    const adminNavigation = inject(injectTypes.adminNavigation, null);
    const { register } = useDetailBreadcrumb();

    const registerBreadcrumbTitle = getTitle => register(() => {
        const title = getTitle();
        if (!title) {
            return { trail: [] };
        }

        const parentName = route.meta?.breadcrumbParent;
        const nav = adminNavigation ?? {};
        // Flatten creator/platform to drop their wrapper group; project keeps its groups as real crumbs.
        const flatten = items => (items ?? []).flatMap(group => group.subItems ?? []);
        const sections = route.params.project
            ? [nav.projectItems, flatten(nav.creatorItems), flatten(nav.platformItems)]
            : route.params.creator
                ? [flatten(nav.creatorItems), flatten(nav.platformItems)]
                : [flatten(nav.platformItems)];

        for (const items of sections) {
            const parentTrail = findNavTrail(items, parentName);
            if (parentTrail) {
                return { trail: [...parentTrail, crumb(title)] };
            }
        }

        return { trail: [crumb(title)] };
    });

    return { registerBreadcrumbTitle };
}
